import { useEffect, useRef, useState } from 'react'
import { getApiErrorMessage, hideMedia, restoreMedia } from '../api'
import { useI18n } from '../i18n'
import { useToast } from '../contexts/ToastContext'
import ConfirmModal from './ConfirmModal'
import '../styles/components/media-presentation.css'

/** Recoverable library removal only. Never deletes the platform original. */
export default function MediaLibraryButton({ modelId, item, onSaved }) {
  const { t } = useI18n()
  const { addToast } = useToast()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const pending = useRef(false)
  const scope = `${modelId}:${item?.id}`
  const currentScope = useRef(scope)
  currentScope.current = scope
  useEffect(() => {
    currentScope.current = scope
    setConfirming(false)
    setBusy(false)
    setError('')
    return () => { currentScope.current = null }
  }, [scope])
  const restore = !!item?.is_hidden
  const label = t(restore ? 'media.library.restore' : 'media.library.remove')
  const blocked = restore && !!item?.provider_missing_since

  async function confirm() {
    if (pending.current === scope || !modelId || !item?.id || blocked) return
    pending.current = scope
    setBusy(true)
    setError('')
    try {
      const updated = await (restore ? restoreMedia : hideMedia)(modelId, item.id)
      if (currentScope.current !== scope) return
      if (typeof updated?.is_hidden !== 'boolean' || updated.is_hidden !== !restore) {
        throw new Error(t('media.library.error'))
      }
      onSaved?.({ ...item, ...updated, _visibility_changed: true })
      setConfirming(false)
      addToast(t(restore ? 'media.library.restored' : 'media.library.removed'), 'success')
    } catch (err) {
      if (currentScope.current === scope) setError(getApiErrorMessage(err, t('media.library.error')))
    } finally {
      if (pending.current === scope) pending.current = false
      if (currentScope.current === scope) setBusy(false)
    }
  }

  if (!onSaved || !item?.id) return null
  return (
    <div className="media-library-action" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      <button type="button" className="media-library-button" disabled={busy || blocked}
        title={blocked ? t('media.library.providerMissing') : label}
        onClick={() => { setError(''); setConfirming(true) }}>
        {label}
      </button>
      <ConfirmModal open={confirming} title={label}
        message={<>{t(restore ? 'media.library.restoreHint' : 'media.library.removeHint')}{error ? <span className="media-library-error" role="alert">{error}</span> : null}</>}
        confirmLabel={busy ? t('common.saving') : label} cancelLabel={t('common.cancel')}
        onConfirm={confirm} onCancel={() => { if (pending.current !== scope) setConfirming(false) }}
        danger={!restore} confirmDisabled={busy} />
    </div>
  )
}
